import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
// GiDiamondRing is from Game Icons (https://game-icons.net), CC BY 3.0.
import { GiDiamondRing } from 'react-icons/gi';

const PHONE_QUERY = '(max-width: 599.95px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const DEG_PER_PX = 1;
const IDLE_MS = 150;
const SPIN_MS = 3000;

type Subscriber = (angle: number, scrolling: boolean) => void;

const subscribers = new Set<Subscriber>();
let scroller: HTMLElement | Window | null = null;
let lastTop = 0;
let angle = 0;
let idleTimer = 0;
let phoneQuery: MediaQueryList | null = null;
let returning = false;
let readVisualAngle: (() => number) | null = null;

function wrapDegrees(deg: number): number {
  const wrapped = ((deg % 360) + 360) % 360;
  return wrapped > 180 ? wrapped - 360 : wrapped;
}

function returnDuration(deg: number): number {
  return (Math.abs(deg) / 360) * SPIN_MS;
}

function readRotateX(el: HTMLElement): number {
  const transform = getComputedStyle(el).transform;
  if (!transform || transform === 'none') return 0;
  const match = transform.match(/matrix3d\(([^)]+)\)/);
  if (!match) return 0;
  const values = match[1].split(',').map((part) => Number(part.trim()));
  if (values.length < 7 || values.some((value) => Number.isNaN(value))) return 0;
  return Math.atan2(values[6], values[5]) * (180 / Math.PI);
}

function prefersReducedMotion(): boolean {
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

function scrollTopOf(target: HTMLElement | Window): number {
  return target === window ? window.scrollY : target.scrollTop;
}

function pageScroller(): HTMLElement | Window {
  const root = document.getElementById('root');
  const phone = window.matchMedia(PHONE_QUERY).matches;
  return phone && root ? root : window;
}

function publish(scrolling: boolean) {
  subscribers.forEach((notify) => notify(angle, scrolling));
}

function onScroll() {
  if (!scroller || prefersReducedMotion()) return;
  const top = scrollTopOf(scroller);
  const dy = top - lastTop;
  lastTop = top;
  if (dy === 0) return;
  if (returning && readVisualAngle) angle = readVisualAngle();
  returning = false;
  angle += dy * DEG_PER_PX;
  publish(true);
  window.clearTimeout(idleTimer);
  idleTimer = window.setTimeout(() => {
    angle = wrapDegrees(angle);
    returning = true;
    publish(false);
  }, IDLE_MS);
}

function bindScroller() {
  if (scroller) scroller.removeEventListener('scroll', onScroll);
  scroller = pageScroller();
  lastTop = scrollTopOf(scroller);
  scroller.addEventListener('scroll', onScroll, { passive: true });
}

function onPhoneChange() {
  bindScroller();
}

function ensureListening() {
  if (scroller) return;
  bindScroller();
  phoneQuery = window.matchMedia(PHONE_QUERY);
  phoneQuery.addEventListener('change', onPhoneChange);
}

function stopListening() {
  if (subscribers.size > 0) return;
  window.clearTimeout(idleTimer);
  if (scroller) scroller.removeEventListener('scroll', onScroll);
  scroller = null;
  phoneQuery?.removeEventListener('change', onPhoneChange);
  phoneQuery = null;
}

function subscribe(notify: Subscriber) {
  subscribers.add(notify);
  ensureListening();
  return () => {
    subscribers.delete(notify);
    stopListening();
  };
}

function settle(tilt: HTMLElement, svg: SVGSVGElement) {
  tilt.style.transition = '';
  tilt.style.transform = '';
  svg.style.animation = '';
}

export default function DiamondRingMark({ size = 20 }: { size?: number }) {
  const tiltRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tilt = tiltRef.current;
    const svg = tilt?.querySelector('svg') ?? null;
    if (!tilt || !svg || prefersReducedMotion()) return;
    let generation = 0;
    let onEnd: ((event: TransitionEvent) => void) | null = null;
    const read = () => readRotateX(tilt);
    readVisualAngle = read;

    const unsubscribe = subscribe((nextAngle, scrolling) => {
      generation += 1;
      const gen = generation;
      if (onEnd) {
        tilt.removeEventListener('transitionend', onEnd);
        onEnd = null;
      }
      if (prefersReducedMotion()) {
        settle(tilt, svg);
        return;
      }
      if (scrolling) {
        svg.style.animation = 'none';
        tilt.style.transition = 'none';
        tilt.style.transform = `rotateX(${nextAngle}deg)`;
        return;
      }

      const wrapped = wrapDegrees(nextAngle);
      svg.style.animation = 'none';
      tilt.style.transition = 'none';
      tilt.style.transform = `rotateX(${wrapped}deg)`;
      if (Math.abs(wrapped) < 0.5) {
        settle(tilt, svg);
        return;
      }

      const duration = returnDuration(wrapped);
      requestAnimationFrame(() => {
        if (gen !== generation) return;
        tilt.style.transition = `transform ${duration}ms linear`;
        tilt.style.transform = 'rotateX(0deg)';
      });

      onEnd = (event: TransitionEvent) => {
        if (event.propertyName !== 'transform' || gen !== generation) return;
        tilt.removeEventListener('transitionend', onEnd!);
        onEnd = null;
        settle(tilt, svg);
      };
      tilt.addEventListener('transitionend', onEnd);
    });

    return () => {
      generation += 1;
      if (onEnd) tilt.removeEventListener('transitionend', onEnd);
      if (readVisualAngle === read) readVisualAngle = null;
      unsubscribe();
    };
  }, []);

  return (
    <Box
      sx={{
        color: 'secondary.main',
        display: 'flex',
        lineHeight: 1,
        perspective: 80,
        '@keyframes ringSpinY': {
          from: { transform: 'rotateY(0deg)' },
          to: { transform: 'rotateY(360deg)' },
        },
      }}
    >
      <Box
        ref={tiltRef}
        sx={{
          display: 'flex',
          transformStyle: 'preserve-3d',
          '& svg': {
            transformBox: 'fill-box',
            transformOrigin: 'center',
            animation: `ringSpinY ${SPIN_MS}ms linear infinite`,
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
          },
        }}
      >
        <GiDiamondRing size={size} />
      </Box>
    </Box>
  );
}
